<!--
@component Port of Portal component from @rgossiaux/svelte-headlessui.

For most UI components, we use implementations from melt-ui, which provide internal portals.
However, sometimes we need portals for our own custom components. In that case we fall back to this
short implementation of portals.

Children of a Portal component are rendered in #modal-portal-root.
-->

<script lang="ts">
  import { tick } from "svelte";

  function portal(
    element: HTMLElement,
    target: HTMLElement | null | undefined,
  ) {
    if (!target) return;
    target.appendChild(element);
    return {
      update(newTarget: HTMLElement) {
        target = newTarget;
        newTarget.append(element);
      },
      destroy() {
        // Need to detach ourselves--we can't rely on Svelte always detaching
        // us since we moved in the component tree.
        if (target?.contains(element)) {
          target.removeChild(element);
        }
        if (target && target.childNodes.length <= 0) {
          target.parentElement?.removeChild(target);
        }
      },
    };
  }
  $: target = (() => {
    if (typeof window === "undefined") return null;
    let existingRoot = document.getElementById("modal-portal-root");
    if (existingRoot) return existingRoot;
    let root = document.createElement("div");
    root.setAttribute("id", "modal-portal-root");
    // During initial render, the "portal" might be constructed before
    // the root component has even attached, causing the portal to not work.
    // This is a workaround for this issue--it can't guarantee the portal is
    // **always** last, but it should catch the normal cases.
    tick().then(() => {
      if (root !== document.body.lastChild) {
        document.body.appendChild(root);
      }
    });
    return document.body.appendChild(root);
  })();
</script>

<div use:portal={target}>
  <slot />
</div>
